import { CheckCircle, Dumbbell, Lightbulb } from 'lucide-react';

import type { QuestionAIState } from '@/features/ai-homework/aiHomeworkTypes';

import type { QuestionInfoPanelTab } from './InfoPanelTabBar';
import type { NormalizedDisclosureData } from './questionNormalizer';

interface AIHomeworkVisibility {
  showHintButton: boolean;
  showPracticeButton: boolean;
}

interface InfoPanelTabParams {
  aiState?: QuestionAIState;
  aiVisibility?: AIHomeworkVisibility;
  hintDisclosure?: NormalizedDisclosureData;
  /** Hide Stats and Solutions is on: no hint of either kind (issue #174). */
  hintsHidden?: boolean;
  solutionDisclosure?: NormalizedDisclosureData;
}

function hasVisibleDisclosure(disclosure?: NormalizedDisclosureData) {
  return Boolean(disclosure?.isVisible && disclosure.units.length > 0);
}

function hasHintState(aiState?: QuestionAIState) {
  const hint = aiState?.hint;

  return Boolean(
    hint?.isLoading ||
      hint?.isRegenerating ||
      hint?.error ||
      hint?.statusMessage ||
      hint?.response,
  );
}

function hasPracticeState(aiState?: QuestionAIState) {
  const practice = aiState?.practice;

  return Boolean(
    practice?.isLoading ||
      practice?.isRegenerating ||
      practice?.error ||
      practice?.statusMessage ||
      practice?.question,
  );
}

export function getInfoPanelTabs({
  aiState,
  aiVisibility,
  hintDisclosure,
  hintsHidden = false,
  solutionDisclosure,
}: InfoPanelTabParams): QuestionInfoPanelTab[] {
  const tabs: QuestionInfoPanelTab[] = [];
  const hasBuiltInHint = hasVisibleDisclosure(hintDisclosure);
  const hasAIHint =
    Boolean(aiVisibility?.showHintButton) || hasHintState(aiState);
  const showHintTab = !hintsHidden && (hasBuiltInHint || hasAIHint);
  const showPracticeTab =
    Boolean(aiVisibility?.showPracticeButton) || hasPracticeState(aiState);

  if (showHintTab) {
    tabs.push({
      icon: Lightbulb,
      id: 'hint',
      label: hasAIHint && !hasBuiltInHint ? 'AI Hint' : 'Hint',
    });
  }

  if (showPracticeTab) {
    tabs.push({
      icon: Dumbbell,
      id: 'practice',
      label: 'Practice',
    });
  }

  if (hasVisibleDisclosure(solutionDisclosure)) {
    tabs.push({
      icon: CheckCircle,
      id: 'solution',
      label: 'Solution',
    });
  }

  return tabs;
}

export function useInfoPanelTabs(params: InfoPanelTabParams) {
  return getInfoPanelTabs(params);
}
